import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import { StyledTableCell, StyledTableRow } from "../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import AppButton from "../common/buttons/AppButton";
import { observer } from "mobx-react";
import {
  PodCatalogProduct,
  PodCatalogSortBy,
  PodCatalogSortType,
} from "../../../models/Products/PodCatalog";
import { Link } from "react-router-dom";

interface PodProductTableProps {
  maxHeight: number;
  items: PodCatalogProduct[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: PodCatalogSortBy;
  sortType: PodCatalogSortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (
    column: PodCatalogSortBy,
    sortType: PodCatalogSortType
  ) => void;
  onRemoveFromPod: (productId: number) => void;
  removingProductId?: number | null;
}

const toggleSortType = (
  sortByCol: PodCatalogSortBy,
  column: PodCatalogSortBy,
  sortType: PodCatalogSortType
): PodCatalogSortType =>
  sortByCol === column && sortType === "DESC" ? "ASC" : "DESC";

const PodProductTable = ({
  maxHeight,
  items,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onRemoveFromPod,
  removingProductId,
}: PodProductTableProps) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: PodCatalogSortBy) => {
    if (sortBy === property) return sortType === "ASC" ? "↑" : "↓";
    return "↑↓";
  };

  const formatDate = (d: Date | string | null) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  };

  const thumbUrl = (row: PodCatalogProduct) => row.images?.[0] || undefined;

  return (
    <Paper sx={{ marginTop: "0.6rem" }}>
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell
                sx={{
                  textAlign: "center",
                  fontSize: 14,
                  position: "sticky",
                  left: 0,
                  zIndex: 30,
                  top: 0,
                  backgroundColor: theme.palette.customColors.white,
                  boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                }}
              >
                <TableSortLabel
                  active={sortBy === "product_id"}
                  direction={sortType === "ASC" ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "product_id",
                      toggleSortType("product_id", sortBy, sortType)
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("product_id")}</span>
                  )}
                  sx={{ fontSize: "12px" }}
                >
                  Product ID
                </TableSortLabel>
              </StyledTableCell>
              <AppTableHeaderCell minWidth="250px">
                <TableSortLabel
                  active={sortBy === "product_name"}
                  direction={sortType === "ASC" ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "product_name",
                      toggleSortType("product_name", sortBy, sortType)
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("product_name")}</span>
                  )}
                  sx={{ fontSize: "12px" }}
                >
                  Name
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell title="SKU" fontSize="12px" />
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "selling_price"}
                  direction={sortType === "ASC" ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "selling_price",
                      toggleSortType("selling_price", sortBy, sortType)
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("selling_price")}</span>
                  )}
                  sx={{ fontSize: "12px" }}
                >
                  POD selling price
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell title="Category" fontSize="12px" />
              <AppTableHeaderCell title="Origin" fontSize="12px" />
              <AppTableHeaderCell title="Prod. days" fontSize="12px" />
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "modified_date"}
                  direction={sortType === "ASC" ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange(
                      "modified_date",
                      toggleSortType("modified_date", sortBy, sortType)
                    )
                  }
                  IconComponent={() => (
                    <span>{renderSortIcon("modified_date")}</span>
                  )}
                  sx={{ fontSize: "12px" }}
                >
                  Modified
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell title="Active" fontSize="12px" />
              <AppTableHeaderCell
                title="Actions"
                minWidth="120px"
                fontSize="12px"
                sx={{
                  color: theme.palette.customColors.tableGrayHeadingColor,
                  position: "sticky",
                  zIndex: 30,
                  top: 0,
                  backgroundColor: theme.palette.customColors.white,
                  minWidth: "100px",
                  right: 0,
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => {
              const img = thumbUrl(row);
              return (
                <StyledTableRow key={row.id}>
                  <TableCell
                    sx={{
                      fontSize: "12px",
                      textAlign: "center",
                      left: 0,
                      position: "sticky",
                      zIndex: 15,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor: `${theme.palette.background.paper}`,
                    }}
                  >
                    <Link to={`/products/pod-catalog/${row.product_id}`}>
                      {row.product_id}
                    </Link>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          position: "relative",
                          background: "rgba(242, 242, 242, 1)",
                          padding: 1,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          marginRight: 2,
                          minHeight: "70px",
                          maxHeight: "70px",
                          borderRadius: "4px",
                          minWidth: "55px",
                        }}
                      >
                        {img ? (
                          <img
                            style={{
                              maxHeight: "55px",
                              minHeight: "55px",
                              maxWidth: "55px",
                              minWidth: "55px",
                              objectFit: "contain",
                            }}
                            src={img}
                            alt={row.product_name || ""}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: "left" }}>
                        {row.product_name || "—"}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.product_sku || "—"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.swageazy_selling_price ?? "—"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.categories?.map((c) => c.name).join(", ") || "N/A"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.country_of_origin || "—"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.production_time ?? "—"}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {formatDate(row.modified_date)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", fontSize: "12px" }}>
                    {row.active === 1 ? "True" : "False"}
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "12%",
                      fontSize: "12px",
                      color: theme.palette.customColors.tableGrayHeadingColor,
                      position: "sticky",
                      zIndex: 10,
                      minWidth: "100px",
                      right: 0,
                      background: theme.palette.customColors.white,
                    }}
                  >
                    <AppButton
                      variant="transparentPrimary"
                      label="Remove"
                      disabled={removingProductId === row.product_id}
                      customStyles={{
                        fontSize: "0.75rem",
                        padding: "4px 8px",
                        minWidth: "auto",
                      }}
                      onClick={() => onRemoveFromPod(row.product_id)}
                    />
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        fontSize="12px"
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={handleRowsChange}
      />
    </Paper>
  );
};

export default observer(PodProductTable);
